import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useMemo, useState } from 'react'
import SizeInputWithUnit, { convert, Unit } from 'core/components/SizeInputWithUnit'
import Info from 'core/components/validatedForm/Info'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { customValidator, nonDecimalValueValidator } from 'core/utils/fieldValidators'
import { getCurrentUser } from 'openstack/helpers'
import { hotplugVirtualMachine } from './actions'
import { isHotplugMaxSet } from './CreateVirtualMachineModal.helpers'
import { hasOtherUserVm, isHotplugVm } from './helpers'

export const HOTPLUG_DIALOG_MESSAGES = {
  BODY_WITH_MAX:
    'Set new values for memory and CPU. These values must be set between the current and maximum values.',
  BODY_WITHOUT_MAX:
    'Set new values for memory and CPU. These values must be at least the current values. No hotplug maximums are set for this VM, so the host maximums apply.',
  MAX_NOT_SET: 'Max: not set (host maximum applies)',
  MEMORY_AT_LEAST_ONE: 'Memory must be at least 1',
  MEMORY_AT_LEAST_CURRENT: 'Memory must be greater than or equal to the current value',
  MEMORY_BETWEEN_ONE_AND_MAX: 'Memory must be between 1 and max values',
  MEMORY_BETWEEN_CURRENT_AND_MAX: 'Memory must be between the current and max values',
  VCPU_AT_LEAST_ONE: 'vCPUs must be at least 1',
  VCPU_AT_LEAST_CURRENT: 'vCPUs must be greater than or equal to the current value',
  VCPU_BETWEEN_ONE_AND_MAX: 'vCPUs must be between 1 and max values',
  VCPU_BETWEEN_CURRENT_AND_MAX: 'vCPUs must be between the current and max values',
}

// HOTPLUG-FR-002, HOTPLUG-FR-003: pick the validation error message for the applicable
// bounds — lower bound is 1 for a stopped (cold-plugged) VM, otherwise the current value;
// the upper bound only exists when the VM has *_MAX metadata.
export const getMemoryValidationMessage = (hasMax: boolean, isColdPlugged: boolean) => {
  if (!hasMax) {
    return isColdPlugged
      ? HOTPLUG_DIALOG_MESSAGES.MEMORY_AT_LEAST_ONE
      : HOTPLUG_DIALOG_MESSAGES.MEMORY_AT_LEAST_CURRENT
  }
  return isColdPlugged
    ? HOTPLUG_DIALOG_MESSAGES.MEMORY_BETWEEN_ONE_AND_MAX
    : HOTPLUG_DIALOG_MESSAGES.MEMORY_BETWEEN_CURRENT_AND_MAX
}

export const getVcpuValidationMessage = (hasMax: boolean, isColdPlugged: boolean) => {
  if (!hasMax) {
    return isColdPlugged
      ? HOTPLUG_DIALOG_MESSAGES.VCPU_AT_LEAST_ONE
      : HOTPLUG_DIALOG_MESSAGES.VCPU_AT_LEAST_CURRENT
  }
  return isColdPlugged
    ? HOTPLUG_DIALOG_MESSAGES.VCPU_BETWEEN_ONE_AND_MAX
    : HOTPLUG_DIALOG_MESSAGES.VCPU_BETWEEN_CURRENT_AND_MAX
}

// HOTPLUG-FR-001, HOTPLUG-FR-003: field label shows the current value and either the
// metadata maximum or the host-maximum fallback note. Current/max always display in MiB
// (the metadata unit) even though the input accepts MiB or GiB.
export const getHotplugMemoryLabel = (
  currentMemory: string,
  memoryMax?: string,
  unit: Unit = 'MiB',
) => {
  const convertedCurrent =
    unit === 'MiB' ? Number(currentMemory) : convert(Number(currentMemory), 'MiB', unit)

  const maxNum = memoryMax ? Number(memoryMax) : null
  const convertedMax = unit === 'MiB' || !maxNum ? maxNum : convert(maxNum, 'MiB', unit)

  const roundedCurrent = convertedCurrent === null ? '' : Math.round(convertedCurrent * 100) / 100
  const roundedMax = convertedMax === null ? '' : Math.round(convertedMax * 100) / 100

  return isHotplugMaxSet(memoryMax)
    ? `Hotplug RAM - Current ${roundedCurrent} ${unit}, Max ${roundedMax} ${unit}`
    : `Hotplug RAM - Current ${roundedCurrent} ${unit}, ${HOTPLUG_DIALOG_MESSAGES.MAX_NOT_SET}`
}

export const getHotplugVcpuLabel = (currentVcpus: string, vcpusMax?: string) =>
  isHotplugMaxSet(vcpusMax)
    ? `Hotplug vCPUs - Current ${currentVcpus}, Max ${vcpusMax}`
    : `Hotplug vCPUs - Current ${currentVcpus}, ${HOTPLUG_DIALOG_MESSAGES.MAX_NOT_SET}`

// HOTPLUG-FR-002: bounds check for the memory value. The memory field uses SizeInputWithUnit,
// so the on-screen value is in the user-selected unit (MiB or GiB) while params hold the
// MiB-converted number — validate the converted MiB value, not the raw field value.
export const isMemoryWithinBounds = (memoryMiB: number, minMiB: number, maxMiB?: number) =>
  maxMiB ? memoryMiB >= minMiB && memoryMiB <= maxMiB : memoryMiB >= minMiB

const ramOptions = [
  { label: 'MiB', value: 'MiB' as Unit },
  { label: 'GiB', value: 'GiB' as Unit },
]

export default function hotplugVmDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const isHotPluggedVm = isHotplugVm(vm)
  const isColdPluggedVm = vm?.state === 'stopped'
  const currentMemory = vm?.metadata?.['HOTPLUG_MEMORY']
  const memoryMax = vm?.metadata?.['HOTPLUG_MEMORY_MAX']
  const currentVcpus = vm?.metadata?.['HOTPLUG_CPU']
  const vcpusMax = vm?.metadata?.['HOTPLUG_CPU_MAX']

  const isHotColdPlugVm = isHotPluggedVm && isColdPluggedVm
  const memoryMin = isHotColdPlugVm ? 1 : currentMemory
  const vcpuMin = isHotColdPlugVm ? 1 : currentVcpus

  const [memoryUnit, setMemoryUnit] = useState<Unit>('MiB')

  const memoryMessage = getMemoryValidationMessage(isHotplugMaxSet(memoryMax), isHotColdPlugVm)
  const vcpuMessage = getVcpuValidationMessage(isHotplugMaxSet(vcpusMax), isHotColdPlugVm)

  const defaultParams = {
    memory: null,
    vcpus: null,
  }
  const { params, getParamsUpdater, updateParams } = useParams(defaultParams)

  const memoryValidator = useMemo(
    () =>
      customValidator(
        (value) =>
          isMemoryWithinBounds(
            Number(value),
            Number(memoryMin),
            isHotplugMaxSet(memoryMax) ? Number(memoryMax) : undefined,
          ),
        memoryMessage,
      ),
    [memoryMin, memoryMax, memoryMessage],
  )

  const vcpuValidator = useMemo(
    () =>
      customValidator((value) => {
        return isHotplugMaxSet(vcpusMax)
          ? value >= vcpuMin && value <= Number(vcpusMax)
          : value >= vcpuMin
      }, vcpuMessage),
    [vcpuMin, vcpusMax, vcpuMessage],
  )

  const { update, updating, error } = useUpdateAction(hotplugVirtualMachine)
  const submitForm = useCallback(async () => {
    const body = {
      hotplug: {
        cpu: params.vcpus,
        memory_mb: params.memory,
      },
    }
    const { success } = await update({
      id: vm.id,
      body,
    })
    if (success) onClose(true)
  }, [vm, params])

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      open
      panel="dialog"
      title={`Hotplug VM`}
      onSubmit={submitForm}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Hotplug VM`}
      maxWidth={600}
    >
      <div className={classes.body}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          {isHotplugMaxSet(memoryMax) || isHotplugMaxSet(vcpusMax)
            ? HOTPLUG_DIALOG_MESSAGES.BODY_WITH_MAX
            : HOTPLUG_DIALOG_MESSAGES.BODY_WITHOUT_MAX}
        </Text>
        <SizeInputWithUnit
          id="memory"
          label={getHotplugMemoryLabel(currentMemory, memoryMax, memoryUnit)}
          onChange={(val) => updateParams({ memory: val })}
          onUnitChange={setMemoryUnit}
          value={params.memory}
          defaultUnit="MiB"
          min={1}
          info="The memory that will be hotplugged to the VM."
          validations={[memoryValidator, nonDecimalValueValidator()]}
          required
          items={ramOptions}
        />
        <TextField
          id="vcpus"
          label={getHotplugVcpuLabel(currentVcpus, vcpusMax)}
          onChange={(val) => updateParams({ vcpus: val })}
          value={params.vcpus}
          info="The number of vcpus that will be hotplugged to the VM."
          type="number"
          validations={[vcpuValidator, nonDecimalValueValidator()]}
          required
        />
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  body: {
    display: 'grid',
    gap: 12,
  },
}))
